<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Resource" avatarImage="/glass/img/tasks-overview-resource.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>{{resource.name}}</h4>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <span v-if="resource.isActive" class="status-active">Active</span>
              <span v-if="!resource.isActive" class="status-archive">Archived</span>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Highlight">
              <LabelBadge :labelColour="resource.highlightColour">{{resource.name}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Type">{{resource.type | formatLookupName(ResourceTypeLookup)}}</OverviewBoxListItem>
            <OverviewBoxListItem title="User Link" v-if="isUserType"><LinkUser :id="resource.userId" :name="resource.user.name" v-if="resource.user" /></OverviewBoxListItem>
            <OverviewBoxListItem title="Groups" v-if="resource.resourceGroups">
              <template v-for="(group, groupIndex) of resource.resourceGroups">
                <br v-if="groupIndex > 0" :key="`sep-${group.id}`" />
                <LinkResourceGroup :key="group.id" :id="group.id" :name="group.name" class="float-right" />
              </template>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{resource.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{resource.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-resource-summary">
              <i class="far fa-clipboard-list-check"></i> Resource Details
            </Tab>
            <Tab tabid="tab-resource-documents">
              <i class="far fa-book"></i> Documents
            </Tab>
            <Tab tabid="tab-resource-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>
          <template v-slot:tabcontent>
            
            <TabPage :first="true" tabid="tab-resource-summary">
              
              <DataPageArea title="Details">
                <template slot="actionArea">
                  <ActionDropMenu class="float-right">
                    <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)" @click="editResource" icon="fa-pencil">Edit Resource</DropdownMenuItem>
                    <DropdownMenuDivider />
                    <DropdownMenuItem @click="archiveResource" v-if="resource.isActive && userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)" icon="fa-archive">Archive Resource</DropdownMenuItem>
                    <DropdownMenuItem @click="restoreResource" v-if="!resource.isActive && userHasUpdatePermission(PermissionTypes.TaskPermissionTypes.taskConfig)" icon="fa-undo">Restore Resource</DropdownMenuItem>
                  </ActionDropMenu>
                </template>
                <template slot="dataArea">
                  <DataItem title="Name">{{resource.name}}</DataItem>
                  <DataItem title="Type">{{resource.type | formatLookupName(ResourceTypeLookup)}}</DataItem>
                  <DataItem title="Operating Unit Type">{{resource.operatingUOMType | formatLookupName(UOMTypeLookup)}}</DataItem>
                  <DataItem title="Operating Cost">{{resource.operatingCostPerUnit | formatCurrency}} / <span v-html="$options.filters.formatLookupName(resource.operatingUOMType, UOMTypeUnitsLookup)" /></DataItem>
                </template>
              </DataPageArea>
            </TabPage>


            <TabPage tabid="tab-resource-documents">
              <div class="row">
                <div class="col-lg-12">
                  <DocumentList
                    title="Documents"
                    linkType="LinkTypes.resource"
                    :linkId="resource.id"
                    :permissionResource="PermissionTypes.TaskPermissionTypes.taskConfig"
                    class="mt-2" />
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-resource-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.resource" :linkId="resource.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'

import DocumentList from '@/components/documentList.vue'
import ResourceDialog from '@/modules/tasks/views/resourceDialog'

import { LinkTypes, ResourceTypes, ResourceTypeLookup, UOMTypeLookup, UOMTypeUnitsLookup } from '@/store/types'

export default {
  name: 'Resource',
  mixins: [form],
  components: {
    DocumentList,
  },
  data() {
    return {
      resource: {},
    }
  },
  computed: {
    activityStreamURL() {
      return `/tasks/resources/${this.$route.params.id}/activitystream`
    },
    isUserType() {
      return this.resource.type == ResourceTypes.user
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.ResourceTypeLookup = ResourceTypeLookup
    this.UOMTypeLookup = UOMTypeLookup
    this.UOMTypeUnitsLookup = UOMTypeUnitsLookup
  },
  mounted() {
    this.requestData()
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.pageLoadData(this.$apiService.tasksService.getResource(this.$route.params.id))
      .then(response => {
        this.resource = response.data
      })
      .catch(err => this.handlePageLoadError(err))
    },

    editResource() {
      this.displayModal(ResourceDialog, { 
        resourceId: this.$data.resource.id,
      },
      this.$data.resource)
      .then((success) => {
        if(success) {
          this.successToast('Resource Updated')
          this.reloadData()
        }
      })
    },
    archiveResource() {
      this.confirmDeleteMessage('Archive Resource?', 'Are you sure you want to archive this resource?')
      .then(success => {
        if(success) {
          this.$apiService.tasksService.archiveResource(this.$route.params.id).then(response => {
            this.successToast('Resource Archived')
            this.reloadData()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not archive resource') })
        }
      })
    },
    restoreResource() {
      this.confirmDeleteMessage('Restore Resource?', 'Are you sure you want to restore this resource?')
      .then(success => {
        if(success) {
          this.$apiService.tasksService.restoreResource(this.$route.params.id).then(response => {
            this.successToast('Resource Restored')
            this.reloadData()
          })
          .catch(err => { this.displayNetworkError(err, 'Error', 'Could not restore resourcen') })
        }
      })
    }
  }
}
</script>